/**
 * Script-scope accumulator for the IntelliSense Engine.
 *
 * Scope-changing statements before the cursor — `USE` (MSSQL/MySQL),
 * `SET search_path` (PG), `ALTER SESSION SET CURRENT_SCHEMA` (Oracle),
 * `ATTACH DATABASE ... AS` (SQLite) — accumulate so completion in later
 * statements sees the resulting database / schema / search-path / attached-db
 * scope. This mirrors what a whole-buffer Execute actually does, so it is
 * always on: a buffer with no scope-changing statements yields the default
 * empty scope.
 *
 * Pure module — string-in / data-out. The renderer-side caller passes the
 * pre-cursor statement texts (one entry per statement) and the engine id;
 * `computeScriptScope` returns a populated `ScriptScope`.
 */

import type { DatabaseEngineId, ScriptScope } from '@/core/engines';

import {
  findEnclosingStatement,
  splitTopLevelStatements,
} from '@/features/intellisense/context/statementBoundaries';

import {
  parseAlterSessionCurrentSchema,
  parseAttachDatabase,
  parseSetSearchPath,
  parseUseDatabase,
  stripLeadingNoise,
} from './scriptScopeParsers';

type MutableScope = {
  currentDatabase?: string;
  currentSchema?: string;
  searchPath?: string[];
  attachedDatabases?: { alias: string; path: string }[];
};

/**
 * Build a `ScriptScope` from the SQL texts of statements that precede the
 * cursor. Statements should be in source order (oldest first); the helper
 * folds them left-to-right so a later `USE` overrides an earlier one.
 *
 * Engine-specific mapping:
 *  - `mssql`, `mysql`: `USE <db>` → `currentDatabase`
 *  - `pgsql`: `SET search_path TO a, b, c` → `searchPath` + `currentSchema = a`
 *  - `oracle`: `ALTER SESSION SET CURRENT_SCHEMA = <name>` → `currentSchema`
 *  - `sqlite`: `ATTACH [DATABASE] '<path>' AS <alias>` → `attachedDatabases`
 *
 * Statements that don't match any rule are ignored. Quoted identifiers
 * (`[…]`, `"…"`, `` `…` ``) are unwrapped; literal database paths in
 * SQLite ATTACH are kept verbatim (single-quoted body, doubled-quote escape).
 */
export function computeScriptScope(
  precedingStatements: readonly string[],
  engineId: DatabaseEngineId,
): ScriptScope {
  const scope: MutableScope = {};

  for (const raw of precedingStatements) {
    const stmt = stripLeadingNoise(raw);
    if (stmt.length === 0) continue;

    if (engineId === 'mssql' || engineId === 'mysql') {
      const db = parseUseDatabase(stmt);
      if (db !== null) scope.currentDatabase = db;
      continue;
    }

    if (engineId === 'pgsql') {
      const path = parseSetSearchPath(stmt);
      if (path !== null) {
        scope.searchPath = path;
        scope.currentSchema = path[0];
      }
      continue;
    }

    if (engineId === 'oracle') {
      const schema = parseAlterSessionCurrentSchema(stmt);
      if (schema !== null) scope.currentSchema = schema;
      continue;
    }

    if (engineId === 'sqlite') {
      const attach = parseAttachDatabase(stmt);
      if (attach !== null) {
        scope.attachedDatabases = [...(scope.attachedDatabases ?? []), attach];
      }
      continue;
    }
  }

  return freeze(scope);
}

/**
 * Convenience wrapper: split the buffer at top-level statement separators,
 * keep only the statements that end before the cursor, and call
 * `computeScriptScope`. The cursor's own (in-progress) statement is excluded
 * — it can't have committed any scope change yet.
 */
export function computeScriptScopeFromBuffer(
  source: string,
  cursorOffset: number,
  engineId: DatabaseEngineId,
): ScriptScope {
  const cursorStmt = findEnclosingStatement(source, cursorOffset, engineId);
  const preceding = splitTopLevelStatements(source, engineId)
    .filter((s) => s.end <= cursorStmt.start)
    .map((s) => source.slice(s.start, s.end));
  return computeScriptScope(preceding, engineId);
}

function freeze(scope: MutableScope): ScriptScope {
  return {
    ...(scope.currentDatabase !== undefined ? { currentDatabase: scope.currentDatabase } : {}),
    ...(scope.currentSchema !== undefined ? { currentSchema: scope.currentSchema } : {}),
    ...(scope.searchPath !== undefined ? { searchPath: Object.freeze([...scope.searchPath]) } : {}),
    ...(scope.attachedDatabases !== undefined
      ? { attachedDatabases: Object.freeze(scope.attachedDatabases.map((d) => ({ ...d }))) }
      : {}),
  };
}
